/*
  © 2025 KondaSoft
  https://www.kondasoft.com
*/
class SlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this.slides = this.querySelectorAll(".slideshow-slide");
    this.currentIndex = 0;
    this.interval = null;

    this.handleControls();
    this.handleAutoplay();
    this.handleTouch();
    this.handleKeyboard();
    this.handleSlidesAccessibility();
    this.animateOnScroll();
    this.handleEditor();
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.update();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.update();
  }

  go(index) {
    this.currentIndex = index;
    this.update();
  }

  update() {
    const offset = -this.currentIndex * 100;
    this.querySelector(".slideshow-track").style.transform = `translateX(${offset}%)`;

    this.slides.forEach((slide, index) => {
      slide.classList.toggle("active", index === this.currentIndex);
    });

    this.querySelectorAll(".slideshow-pagination button").forEach((btn, i) => {
      btn.classList.toggle("active", i === this.currentIndex);
    });

    this.handleSlidesAccessibility();
  }

  handleControls() {
    this.querySelectorAll(".slideshow-control").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.name === "previous") {
          this.prev();
        } else if (btn.name === "next") {
          this.next();
        }
      });
    });

    this.querySelectorAll(".slideshow-pagination button").forEach((btn, index) => {
      btn.addEventListener("click", () => {
        this.go(index);
      });
    });
  }

  handleAutoplay() {
    if (!this.dataset.autoplay || this.dataset.autoplay === "0") return;
    let interval = null;

    const start = () => {
      clearInterval(interval);
      interval = setInterval(() => this.next(), Number(this.dataset.autoplay) * 1000);
      this.dataset.autoplayStopped = "false";
      this.querySelector(".slideshow-track").setAttribute("aria-live", "off");
    };

    const stop = () => {
      clearInterval(interval);
      this.dataset.autoplayStopped = "true";
      this.querySelector(".slideshow-track").setAttribute("aria-live", "polite");
    };

    start();

    this.addEventListener("focusin", () => stop());
    this.addEventListener("focusout", () => start());
    this.addEventListener("mouseenter", () => stop());
    this.addEventListener("mouseleave", () => start());
    this.addEventListener("touchstart", () => stop());
    this.addEventListener("touchend", () => start());

    this.addEventListener("shopify:block:select", (e) => {
      if (e.target.closest(`[data-section-id="${this.dataset.sectionId}"]`)) stop();
    });
  }

  handleKeyboard() {
    this.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.prev();
      else if (event.key === "ArrowRight") this.next();
    });
  }

  handleTouch() {
    let startX = 0;
    let endX = 0;

    this.addEventListener("touchstart", (e) => {
      startX = e.touches[0].clientX;
    });

    this.addEventListener("touchmove", (e) => {
      endX = e.touches[0].clientX;
    });

    this.addEventListener("touchend", () => {
      if (!startX || !endX) return;
      const swipeDistance = startX - endX;

      if (swipeDistance > 10) {
        this.next();
      } else if (swipeDistance < -10) {
        this.prev();
      }
    });
  }

  handleSlidesAccessibility() {
    this.slides.forEach((slide, i) => {
      if (i === this.currentIndex) {
        slide.removeAttribute("aria-hidden");
        slide.removeAttribute("tabindex");
        slide.querySelectorAll("a, button").forEach((el) => {
          el.removeAttribute("tabindex");
        });
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.setAttribute("tabindex", "-1");
        slide.querySelectorAll("a, button").forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
      }
    });
  }

  animateOnScroll() {
    if (this.querySelector(".slideshow-slide").dataset.overlayOpacity === undefined) return;

    window.addEventListener("scroll", () => {
      const top = Math.round(this.getBoundingClientRect().top);
      const bottom = Math.round(this.getBoundingClientRect().bottom);
      const visibilityTop = Math.round(((window.innerHeight - top) / this.clientHeight) * 100);
      const visibilityBottom = Math.round((bottom / this.clientHeight) * 100);

      if (visibilityTop > 0 && visibilityTop < 100) {
        this.querySelectorAll(".slideshow-slide").forEach((elem) => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity);
          const final = Math.round((100 - overlayOpacity) * (1 - visibilityTop / 100) + overlayOpacity);
          elem.style.setProperty("--overlay-opacity", final + "%");
        });
      } else if (visibilityBottom > 0 && visibilityBottom < 100) {
        this.querySelectorAll(".slideshow-slide").forEach((elem) => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity);
          const final = Math.round((100 - overlayOpacity) * (1 - visibilityBottom / 100) + overlayOpacity);
          elem.style.setProperty("--overlay-opacity", final + "%");
        });
      }
    });
  }

  handleEditor() {
    document.addEventListener("shopify:block:select", (event) => {
      if (event.target.closest(`[data-section-id="${this.dataset.sectionId}"]`)) {
        this.go(Number(event.target.dataset.index));
      }
    });
  }
}
customElements.define("slideshow-component", SlideshowComponent);
